// =============================================================================
// @-Mentions — Parsing & Helfer (framework-frei, damit leicht testbar)
//
// Im gespeicherten Text steht eine Erwähnung als Token:  @[Voller Name](uuid)
// Angezeigt wird daraus ein hervorgehobenes „@Voller Name". Diese Datei kapselt
// das Erkennen, Zerlegen und Aufräumen dieser Tokens.
// =============================================================================

// Ein Token: @[Name](36-stellige uuid). Name darf keine ] enthalten.
const TOKEN_RE = /@\[([^\]]+)\]\(([0-9a-fA-F-]{36})\)/g;

// Klartext-Label je Quelle einer Erwähnung (für die Mitteilung in der Glocke).
export const MENTION_SOURCE_LABELS = {
  project_description: 'Projektbeschreibung',
  feedback:           'Feedback',
  project_closure:    'Projektabschluss',
  deadline_overrun:   'Deadline-Stellungnahme',
  checkin:            'Monats-Check-in',
};

// Baut das Speicher-Token für eine Erwähnung.
export function mentionToken(name, id) {
  // Eckige Klammern im Namen entschärfen, damit das Token eindeutig bleibt.
  const safe = String(name || '').replace(/[[\]]/g, '');
  return `@[${safe}](${id})`;
}

// Alle erwähnten User-IDs (einmalig, in Reihenfolge des Auftretens).
export function extractMentionIds(text) {
  if (!text) return [];
  const ids = [];
  for (const m of text.matchAll(TOKEN_RE)) {
    if (!ids.includes(m[2])) ids.push(m[2]);
  }
  return ids;
}

// Ersetzt Tokens durch reines „@Name" — für Exporte, E-Mails, Snippets und
// jeden Kontext, der die Tokens nicht hervorgehoben rendert.
export function stripMentions(text) {
  if (!text) return text ?? '';
  return text.replace(TOKEN_RE, (_, name) => `@${name}`);
}

// Zerlegt den Text in Segmente für die Anzeige:
//   { type: 'text', value } | { type: 'mention', value: '@Name', id }
// So kann eine React-Komponente Erwähnungen hervorheben, ohne die Regex zu kennen.
export function splitMentions(text) {
  const out = [];
  if (!text) return out;
  let last = 0;
  for (const m of text.matchAll(TOKEN_RE)) {
    if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
    out.push({ type: 'mention', value: `@${m[1]}`, id: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
}

// Kurzer, tokenfreier Ausschnitt rund um die erste Erwähnung — für die
// Mitteilung/E-Mail, damit sie ohne Öffnen des Projekts verständlich ist.
export function mentionSnippet(text, max = 140) {
  const plain = stripMentions(text || '').replace(/\s+/g, ' ').trim();
  if (plain.length <= max) return plain;
  return plain.slice(0, max - 1).trimEnd() + '…';
}

// Erkennt eine gerade getippte, noch offene Erwähnung direkt vor dem Cursor.
// Liefert { query, start } wenn der Cursor hinter einem „@wort" steht, sonst null.
// Beispiel: "Hi @val|" → { query: 'val', start: 3 }. Bricht ab, sobald ein
// Leerzeichen/Zeilenumbruch nach dem @ kommt.
export function activeMentionQuery(text, caret) {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  // Vor dem @ muss Zeilenanfang oder Whitespace stehen (keine E-Mail-Adressen).
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const query = upto.slice(at + 1);
  // Sobald Whitespace im Query auftaucht, ist die Erwähnung nicht mehr „offen".
  // (Ein Leerzeichen im Namen wird erst durch Auswahl als Token eingefügt.)
  if (/[\s\n]/.test(query)) return null;
  return { query, start: at };
}
